import { pbQuery } from "@sergio9929/pb-query";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import type { Collections, DeletedResponse } from "@/types/pocketbase";

export type CollectionItem = {
	collection: Collections;
	id: string;
};

type Props = {
	items: Array<CollectionItem>;
	onSuccess: () => void;
};

export function buildUndoDeleteFilter(items: Array<CollectionItem>) {
	return pbQuery<DeletedResponse>().group((q) => {
		for (const item of items) {
			q.equal("recordId", item.id).and().equal("collection", item.collection).or();
		}

		return q;
	});
}

export const useDeleteCollectionItems = () => {
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async ({ items, onSuccess }: Props) => {
			toast.loading(
				t(($) => $.common.components.confirmDialog.delete.status.loading.title),
				{
					id: "delete-record",
					description: t(($) => $.common.components.confirmDialog.delete.status.loading.message),
					action: null,
				}
			);

			const batch = pb.createBatch();

			for (const item of items) {
				batch.collection(item.collection).delete(item.id);
			}

			await batch.send();

			return { items, onSuccess };
		},
		onSuccess: ({ onSuccess }) => {
			toast.success(
				t(($) => $.common.components.confirmDialog.delete.status.success.title),
				{
					id: "delete-record",
					description: t(($) => $.common.components.confirmDialog.delete.status.success.message),
					action: null,
				}
			);

			onSuccess?.();
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, {});

			if (parsed.errorMap.batchErrors.length > 0) {
				const descriptions = parsed.errorMap.batchErrors.flatMap((batchError) => batchError.message);

				toast.error(
					t(($) => $.common.components.confirmDialog.delete.status.error.title),
					{
						id: "delete-record",
						duration: 10_000,
						description: descriptions.join(", "),
						action: null,
					}
				);
				return;
			}

			if (parsed.unknown) {
				toast.error(
					t(($) => $.common.components.confirmDialog.delete.status.error.title),
					{
						id: "delete-record",
						description: parsed.message || t(($) => $.common.components.confirmDialog.delete.status.error.message),
						action: null,
					}
				);
				return;
			}

			toast.dismiss("delete-record");
		},
	});
};
